import { AskByEmailWidget } from "@/components/widget/AskByEmailWidget";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import type { EmailWidgetEmbedProps } from "@/types/ask-by-email";
import { useEmbedCode } from "./hooks/useEmbedCode";
import { ScriptEmbed } from "./ScriptEmbed";
import { IFrameEmbed } from "./IFrameEmbed";
import { ReactEmbed } from "./ReactEmbed";

export const EmailWidgetEmbed = ({ genie }: EmailWidgetEmbedProps) => {
  const update = useUpdateAgent();
  const enabled = genie.branding?.embedStyle?.emailWidgetEnabled === true;
  const mailboxReady = genie.mailbox?.status === "provisioned" && !!genie.mailbox?.email_address;
  const publiclyAccessible = genie.is_active && (genie.is_public || genie.is_marketplace_genie);
  const slug = genie.url_name || genie.page?.url_name;
  const canTest = enabled && mailboxReady && publiclyAccessible && !!slug && !update.isPending;
  const testUrl = `/email-genie/${encodeURIComponent(slug || "")}`;
  const { scriptEmbedCode, iframeEmbedCode, iframeReactCode } = useEmbedCode(
    genie,
    "classic",
    "medium",
    genie.branding?.embedStyle?.widgetPosition || "bottom-right",
    `Email ${genie.name}`,
    undefined,
    "email",
  );

  return (
    <div className="grid items-start gap-6 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-700">Design preview</h3>
          <span className="text-xs text-gray-500">Try it below · No emails sent</span>
        </div>
        <div className="rounded-2xl border border-genie-border bg-genie-background px-3 py-6 sm:px-6 sm:py-8">
          <div className="mx-auto w-full max-w-[400px]">
            <AskByEmailWidget preview genieName={genie.name} branding={{ ...DEFAULT_BRANDING, ...genie.branding }} />
          </div>
        </div>
      </div>

      <div className="min-w-0 space-y-5 rounded-2xl border border-genie-border bg-white p-5 sm:p-6">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Add to your website</h3>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">Your Genie’s logo and colours, ready to go.</p>
        </div>
        <div className="border-y border-genie-border py-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <label htmlFor="enable-email-widget" className="text-sm font-semibold text-gray-900">Accept email questions</label>
            <p className="mt-1 text-sm text-gray-500">Delivered to your Genie’s mailbox.</p>
          </div>
          <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
            <Switch
              id="enable-email-widget"
              checked={enabled}
              disabled={update.isPending || (!enabled && (!mailboxReady || !publiclyAccessible))}
              onCheckedChange={(checked) => update.mutate({
                agent: genie,
                updateData: {
                  branding: {
                    ...DEFAULT_BRANDING,
                    ...genie.branding,
                    embedStyle: { ...genie.branding?.embedStyle, emailWidgetEnabled: checked },
                  },
                },
              })}
            />
          </div>
        </div>
        {!mailboxReady && <p className="mt-3 text-sm text-gray-600">Set up this Genie’s mailbox in its Email settings first.</p>}
        {!publiclyAccessible && <p className="mt-3 text-sm text-gray-600">Make this Genie active and public to accept questions from website visitors.</p>}
        {update.isError && <p role="alert" className="mt-3 text-sm text-genie-error">Couldn’t save. Please try again.</p>}
        </div>
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Try a real email</h3>
          <p className="text-sm leading-relaxed text-gray-600">{canTest
            ? "Open the live widget, enter your own email address, and ask a question. Then check your inbox for the reply."
            : "Switch on Accept email questions above to test a real question and reply."}</p>
          {canTest ? (
            <Button asChild variant="outline" className="min-h-11 gap-2">
              <a href={testUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" aria-hidden="true" /> Test email widget</a>
            </Button>
          ) : (
            <Button disabled variant="outline" className="min-h-11 gap-2"><ExternalLink className="h-4 w-4" aria-hidden="true" /> Test email widget</Button>
          )}
          <p className="text-xs leading-relaxed text-gray-500">The test page sends real email questions to your Genie’s mailbox.</p>
        </div>
        <div className="min-w-0 space-y-4">
          {enabled && mailboxReady && publiclyAccessible ? (
            <Tabs defaultValue="script">
              <TabsList className="grid h-auto w-full grid-cols-3">
                <TabsTrigger value="script" className="min-h-11">JavaScript</TabsTrigger>
                <TabsTrigger value="html" className="min-h-11">HTML</TabsTrigger>
                <TabsTrigger value="react" className="min-h-11">React</TabsTrigger>
              </TabsList>
              <TabsContent value="script"><ScriptEmbed embedCode={scriptEmbedCode} /></TabsContent>
              <TabsContent value="html"><IFrameEmbed embedCode={iframeEmbedCode} /></TabsContent>
              <TabsContent value="react"><ReactEmbed embedCode={iframeReactCode} /></TabsContent>
            </Tabs>
          ) : (
            <p className="text-sm leading-relaxed text-gray-500">Switch on email questions to get your embed code.</p>
          )}
        </div>
      </div>
    </div>
  );
};
